// frontend/packages/business-console/src/components/media/MediaCard.jsx

import React, { useMemo } from 'react';
import { Box, Button, Checkbox, Stack, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Surface, StatusPill, useTokens } from '../primitives';

export default function MediaCard({
  asset,
  onClick,
  onDoubleClick,
  onOpenDetails,
  selectable = false,
  checked = false,
  onToggleSelect,
}) {
  const t = useTokens();
  const url = asset?.publicUrl || asset?.url || asset?.cdnUrl || '';
  const type = String(asset?.type || asset?.mediaType || 'image').toLowerCase();
  const needsAltText = type !== 'video' && !String(asset?.altText || '').trim();

  const title = useMemo(() => {
    return (
      asset?.title ||
      asset?.name ||
      (asset?.blobPath ? asset.blobPath.split('/').pop() : 'Untitled')
    );
  }, [asset?.blobPath, asset?.name, asset?.title]);

  return (
    <Surface
      interactive
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      radius="12px"
      sx={{ p: 1, userSelect: 'none' }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '4 / 3',
          bgcolor: t.subtle,
          borderRadius: '8px',
          overflow: 'hidden',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        {type === 'video' ? (
          <video
            src={url}
            muted
            playsInline
            preload="metadata"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <img
            src={url}
            alt={asset?.altText || ''}
            loading="lazy"
            decoding="async"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
        {selectable ? (
          <Box
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              bgcolor: 'background.paper',
              borderRadius: '6px',
              lineHeight: 0,
            }}
          >
            <Checkbox
              size="small"
              checked={checked}
              onClick={(e) => e.stopPropagation()}
              onChange={() => onToggleSelect?.()}
              inputProps={{ 'aria-label': `Select ${title}` }}
              sx={{ p: 0.5 }}
            />
          </Box>
        ) : null}
        {needsAltText ? (
          <Box sx={{ position: 'absolute', top: 6, left: 6 }}>
            <StatusPill size="sm" kind="warning" label="Needs alt text" />
          </Box>
        ) : null}
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mt: 1 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            noWrap
            sx={{ fontSize: 12.5, fontWeight: 800, color: t.text }}
          >
            {title}
          </Typography>
          <Typography
            noWrap
            sx={{ fontSize: 11, color: t.faint, textTransform: 'capitalize' }}
          >
            {type}
          </Typography>
        </Box>

        <Button
          variant="text"
          size="small"
          startIcon={<InfoOutlinedIcon />}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onOpenDetails?.();
          }}
          sx={{ fontWeight: 800, flexShrink: 0 }}
        >
          Details
        </Button>
      </Stack>
    </Surface>
  );
}
